import { faucetIntentUrl, faucetPostText } from '@/core/faucet-post';
import { faucetCard } from '@/net/api';

import { renderFaucetCard } from './card';
import { canShareFiles, toBase64 } from './share';

/**
 * The faucet's share flow, on the score cards' pipeline (`share.ts`): render the two cards, store them for the link
 * page, hand the story image to the system share sheet where there is one, and where there is not (a desktop, a
 * browser that cannot share files) open X's post composer in a new window — the image then arrives through the
 * `/f/:code` link card, which the server unfurls from the cards uploaded here. Blockari's `faucet-share.ts` (a5e72d1).
 */

export interface FaucetCards {
  story: Blob;
  link: Blob;
}

const UPLOAD_TIMEOUT_MS = 4000;
const FILE_NAME = '21wild-free-tokens.jpg';
const SHARE_TITLE = '21 Wild';

/** Draws both card sizes for a personal link. */
export async function renderFaucetCards(url: string, amount: number): Promise<FaucetCards> {
  const [story, link] = await Promise.all([renderFaucetCard({ size: 'story', amount, url }), renderFaucetCard({ size: 'link', amount, url })]);
  return { story, link };
}

/** Stores the cards for the link page; false on any failure (the share carries on with the generic card). */
export async function uploadFaucetCards(cards: FaucetCards): Promise<boolean> {
  try {
    const [story, link] = await Promise.all([toBase64(cards.story), toBase64(cards.link)]);
    const res = await Promise.race([faucetCard({ story, link }), new Promise<null>((r) => setTimeout(() => r(null), UPLOAD_TIMEOUT_MS))]);
    return res !== null && res.ok;
  } catch {
    return false;
  }
}

/** Whether this browser can hand the story image to a share sheet. */
export function canShareStory(cards: FaucetCards): boolean {
  return canShareFiles([new File([cards.story], FILE_NAME, { type: 'image/jpeg' })]);
}

/** What the native sheet is handed: the image and the post's text, which ENDS with the link. No `url` key, ever. */
export function nativePayload(cards: FaucetCards, link: string, amount: number): ShareData {
  return {
    files: [new File([cards.story], FILE_NAME, { type: 'image/jpeg' })],
    title: SHARE_TITLE,
    // The link rides INSIDE the text and `url` is left out: several share targets (the X app on Android, some iOS
    // versions) drop `url` when files are attached, and a post without the personal link cannot be claimed. It
    // appears exactly once.
    text: faucetPostText(amount, link),
  };
}

/**
 * Shares the post: the native sheet with the story image and the text that ends with the link (phones), else the X
 * composer in a new window (its own text + url fields). Called straight from the tap, with the cards already made,
 * so the share sheet and the new window both keep the tap's permission.
 */
export async function shareFaucet(cards: FaucetCards, link: string, amount: number): Promise<'native' | 'x'> {
  if (canShareStory(cards)) {
    try {
      await navigator.share(nativePayload(cards, link, amount));
      return 'native';
    } catch (err) {
      // Dismissed: nothing more to do. Anything else: the composer still works.
      if (err instanceof Error && err.name === 'AbortError') return 'native';
    }
  }
  window.open(faucetIntentUrl(amount, link), '_blank', 'noopener');
  return 'x';
}
